import { ApplicationConfig } from '@angular/core';
import { ApolloClientOptions, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { TenantService } from '../services/tenant.service';
import { isBootstrapOperation, whenTenantResolved } from './tenant-resolution';

export function provideApolloClients(): ApplicationConfig['providers'] {
  return [
    {
      provide: APOLLO_OPTIONS,
      useFactory(httpLink: HttpLink, auth: AuthService, tenant: TenantService): ApolloClientOptions {
        const errorLink = new ErrorLink(({ error }) => {
          if (CombinedGraphQLErrors.is(error)) {
            error.errors.forEach(({ message, path }) =>
              console.error(`[GraphQL error]: ${message} — path: ${(path as string[] | undefined)?.join('.')}`)
            );
          } else {
            console.error('[Network error]:', error);
          }
        });

        const retryLink = new RetryLink({
          attempts: {
            max: 3,
            retryIf: (error) =>
              !!error && ((error as any).statusCode >= 500 || error.message?.includes('Failed to fetch')),
          },
          delay: { initial: 300, max: 5000, jitter: true },
        });

        const authLink = setContext(async (operation) => {
          if (!isBootstrapOperation(operation.operationName)) {
            try {
              await whenTenantResolved();
            } catch {
              // Gate never throws; defensive only.
            }
          }

          const headers: Record<string, string> = {
            'x-api-key': environment.graphqlApiKey,
          };

          try {
            await auth.waitForAuthReady();
            const token = await auth.getIdToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;
          } catch {
            // Auth timed out — proceed without a token rather than hang forever.
          }

          const tenantId = tenant.activeTenantId();
          if (tenantId) headers['Tenant-ID'] = tenantId;
          return { headers };
        });

        const http = httpLink.create({ uri: environment.graphqlUrl });

        return {
          link: ApolloLink.from([errorLink, retryLink, authLink, http]),
          cache: new InMemoryCache(),
          devtools: { enabled: !environment.production },
          defaultOptions: {
            query: { fetchPolicy: 'network-only', errorPolicy: 'all' },
            watchQuery: { fetchPolicy: 'network-only', errorPolicy: 'all' },
          },
        };
      },
      deps: [HttpLink, AuthService, TenantService],
    },
  ];
}
